import { createContext, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { ProjectIssuanceConstants, ProjectPermissionConstants } from "../../../../constants";
import { parseProjectPhase, ProjectPhase } from "../../../../constants/project-phase";
import { ProjectDates, ProjectDatesData } from "../../../../models";
import {
  getProjectDetails as getProjectDetailsPublicQuery,
  GetProjectDetailsResponse as GetPublicProjectDetailsResponse,
} from "../../../../service/publicquery";
import {
  getCurrentUserProjectPermissions,
  getProjectDetails,
  GetProjectDetailsResponse,
} from "../../../../service/query";
import { Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { jsonReviver } from "../../../../utils";
import { Toast } from "../../../../widget";

type ProjectPageRouteParams = "projectUuid";

interface ProjectProviderProps {
  children?: JSX.Element;
}

interface ProjectContextType {
  projectDates: ProjectDatesData | undefined;
  setProjectDates: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
  projectDetails: GetProjectDetailsResponse | undefined;
  setProjectDetails: Dispatch<SetStateAction<GetProjectDetailsResponse | undefined>>;
  publicProjectDetails: GetPublicProjectDetailsResponse | undefined;
  /**
   * True when the user cannot read the project, so only its public details are loaded (the limited "pre-access"
   * view). Follows the permission check rather than which details loaded, so a failed public load still keeps the
   * user out of the full-access view.
   */
  isPreAccess: boolean;
  isExternalProject: () => boolean;
  hasProjectPermission: (permission: string) => boolean;
  refreshActivities: boolean;
  shouldRefreshActivities: () => void;
  shouldRefreshProjectDetails: () => void;
  groupUuid: string | null;
  /** Issuance statuses this project's inventory surfaces are reporting on (BR-02911). */
  selectedStatuses: string[];
  setSelectedStatuses: (statuses: string[]) => void;
}

export const ProjectContext = createContext<ProjectContextType>({} as ProjectContextType);

export const ProjectProvider = ({ children }: ProjectProviderProps): JSX.Element => {
  const { currentDeveloperUuid } = useAuth();
  const { projectUuid } = useParams<ProjectPageRouteParams>();
  const [isLoading, setIsLoading] = useState(true);
  const [refreshActivities, setRefreshActivities] = useState(false);
  const [refreshProjectDetails, setRefreshProjectDetails] = useState(false);
  const [searchParams] = useSearchParams();
  const groupUuid = searchParams.get("groupuuid");

  const shouldRefreshActivities = (): void => {
    setRefreshActivities((prev) => !prev);
  };

  const shouldRefreshProjectDetails = (): void => {
    setRefreshProjectDetails((prev) => !prev);
  };

  const [projectDetails, setProjectDetails] = useState<GetProjectDetailsResponse | undefined>(undefined);
  const [publicProjectDetails, setPublicProjectDetails] = useState<GetPublicProjectDetailsResponse | undefined>(
    undefined
  );

  const [projectDates, setProjectDates] = useState<ProjectDatesData | undefined>(undefined);
  const [canReadProject, setCanReadProject] = useState<boolean | undefined>(undefined);

  /*
    Shared by this project's inventory surfaces (the Inventory tab's chart and table, its heading
    echo, and the project card's units figure) so they cannot disagree about what is being counted.
    Project-scoped: reset below when the project changes, and never persisted, so a reload returns to
    the Hub default.
  */
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    ProjectIssuanceConstants.ISSUANCE_STATUS_HUB_DEFAULTS
  );

  const fetchData = useCallback(async () => {
    if (projectUuid) {
      const currentUserProjectPermissionsRes = await getCurrentUserProjectPermissions({
        projectUuids: [projectUuid],
        groupUuids: null,
      });

      const hasReadPermission =
        currentUserProjectPermissionsRes.status === Status.Success &&
        currentUserProjectPermissionsRes.data &&
        currentUserProjectPermissionsRes.data[0].currentUserPermissions.some(
          (permission) => permission === ProjectPermissionConstants.READ_PROJECT
        );

      setCanReadProject(!!hasReadPermission);

      if (hasReadPermission) {
        const res = await getProjectDetails({
          projectUuid,
          projectType: null,
          projectExternalReference: null,
        });

        if (res.status === Status.Success && res.data) {
          setProjectDetails(res.data);

          setProjectDates(
            (() => {
              const parsedJsonDates = JSON.parse(res.data.dates, jsonReviver) as ProjectDates;

              return res.data.dates
                ? {
                    uuid: res.data.uuid,
                    rowVersion: res.data.rowVersion,
                    displayName: res.data.displayName,
                    currentStatus: res.data.status,
                    cachedPhase: parseProjectPhase(res.data.cachedPhase) ?? ProjectPhase.feasibility,
                    milestones: undefined,
                    dates: {
                      ...parsedJsonDates,
                      ongoingMonitoring: {
                        ...parsedJsonDates.ongoingMonitoring,
                        cycles:
                          parsedJsonDates.ongoingMonitoring?.cycles?.sort((a, b) => a.sequence - b.sequence) ?? [],
                      },
                    } as ProjectDates,
                    standardSpecificSchemaUuid: res.data.standardSpecificSchemaUuid,
                  }
                : undefined;
            })()
          );
        }
      } else {
        const res = await getProjectDetailsPublicQuery({
          projectUuid,
        });
        setPublicProjectDetails(res.data);

        if (res.status !== Status.Success || !res.data) {
          Toast.error({ message: "Unable to load this project" });
        }
      }
    }
  }, [projectUuid]);

  const isExternalProject = (): boolean => {
    return currentDeveloperUuid !== projectDetails?.developer.uuid;
  };

  const hasProjectPermission = (permission: string): boolean => {
    return projectDetails?.currentUserPermissions?.includes(permission) ?? false;
  };

  // Reset per-project state when switching projects so stale data from the previous
  // project isn't shown, and the no-read public path can't leave the previous
  // project's private details in place (isExternalProject/hasProjectPermission).
  useEffect(() => {
    setIsLoading(true);
    setProjectDetails(undefined);
    setPublicProjectDetails(undefined);
    setCanReadProject(undefined);
    setProjectDates(undefined);
    setSelectedStatuses(ProjectIssuanceConstants.ISSUANCE_STATUS_HUB_DEFAULTS);
  }, [projectUuid]);

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData, projectUuid, refreshProjectDetails]);

  const memoisedValue = useMemo(
    () => ({
      projectDates,
      projectDetails,
      publicProjectDetails,
      isPreAccess: canReadProject === false,
      refreshActivities,
      shouldRefreshActivities,
      shouldRefreshProjectDetails,
      setProjectDates,
      setProjectDetails,
      isExternalProject,
      hasProjectPermission,
      groupUuid,
      selectedStatuses,
      setSelectedStatuses,
    }),
    [projectDetails, publicProjectDetails, canReadProject, projectDates, fetchData, refreshActivities, selectedStatuses]
  );

  return <ProjectContext.Provider value={memoisedValue}>{!isLoading && children}</ProjectContext.Provider>;
};
